import React, { useCallback, useEffect, useRef, useState } from 'react';

const LOCK_BADGE_STYLE = {
  position: 'absolute',
  top: '8px',
  right: '10px',
  pointerEvents: 'none',
  color: '#a67c2c',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontFamily: '"Poppins", sans-serif',
  fontWeight: 600,
};

const LockBadge = () => (
  <div style={LOCK_BADGE_STYLE}>
    <i className="fa-solid fa-lock"></i>Locked
  </div>
);

const OnsiteSignaturePad = ({ className, onChange, height = 160, initialDataUrl, readOnly = false }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const paintBg = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const syncSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const snapshot = hasInkRef.current ? canvas.toDataURL() : null;
    const w = canvas.parentElement?.clientWidth || 400;
    canvas.width = w;
    canvas.height = height;
    paintBg();
    if (snapshot) {
      const img = new Image();
      img.onload = () => canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = snapshot;
    }
  }, [height, paintBg]);

  useEffect(() => {
    syncSize();
    window.addEventListener('resize', syncSize);
    return () => window.removeEventListener('resize', syncSize);
  }, [syncSize]);

  // Draw saved signature after syncSize has set canvas dimensions
  useEffect(() => {
    if (!initialDataUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      hasInkRef.current = true;
      setHasInk(true);
    };
    img.src = initialDataUrl;
  }, [initialDataUrl]);

  const notifyParent = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    onChange(hasInkRef.current ? canvas.toDataURL('image/png') : '');
  }, [onChange]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const start = (e) => {
    if (readOnly) return;
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (readOnly) return;
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    if (!hasInkRef.current) { hasInkRef.current = true; setHasInk(true); }
  };

  const end = (e) => {
    if (e?.preventDefault) e.preventDefault();
    // Stop bubbling to window so dom-autoscroller's onUp handler doesn't fire
    // and trigger its broken animationFramePolyfill.cancelAnimationFrame call
    if (e?.nativeEvent) e.nativeEvent.stopPropagation();
    if (drawing.current) {
      drawing.current = false;
      notifyParent();
    }
  };

  const clear = () => {
    hasInkRef.current = false;
    setHasInk(false);
    syncSize();
    onChange?.('');
  };

  return (
    <div className={className}>
      <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1.5px dashed #d4cfc4', background: '#fcfbf9' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height, touchAction: 'none', cursor: readOnly ? 'default' : 'crosshair', display: 'block' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
        />
        {!readOnly && !hasInk && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', color: '#c8c4ba', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-solid fa-pen-nib"></i>Sign here
          </div>
        )}
        {readOnly && <LockBadge />}
      </div>
      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button type="button" onClick={clear} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 16px', fontSize: '12px', color: '#777', cursor: 'pointer', fontFamily: '"Poppins", sans-serif' }}>
            <i className="fa-solid fa-eraser" style={{ marginRight: '6px' }}></i>Clear Signature
          </button>
        </div>
      )}
    </div>
  );
};

export default OnsiteSignaturePad;
